import { useCallback, useMemo } from "react";

import { useServices } from "@/hooks";
import { Audience } from "@/types";
import { audienceListPath } from "@/utils";

/**
 * Opens the audience dialogs. The dialogs perform the work themselves, so
 * this holds no state and makes no API calls.
 */
export const useAudienceDialogs = (audience?: Audience) => {
  const { ui } = useServices();

  const confirmDelete = useCallback(() => {
    if (!audience) return;
    ui.openDialog({
      type: "deleteAudience",
      props: { audience, redirect: audienceListPath(audience) },
    });
  }, [audience, ui]);

  const confirmShare = useCallback(() => {
    if (!audience) return;
    ui.openDialog({ type: "shareAudience", props: { audience } });
  }, [audience, ui]);

  const confirmUnshare = useCallback(() => {
    if (!audience) return;
    ui.openDialog({ type: "unshareAudience", props: { audience } });
  }, [audience, ui]);

  const confirmClone = useCallback(() => {
    if (!audience) return;
    ui.openDialog({ type: "cloneAudience", props: { audience } });
  }, [audience, ui]);

  const promptRename = useCallback(() => {
    if (!audience) return;
    ui.openDialog({ type: "editAudience", props: { audience } });
  }, [audience, ui]);

  return useMemo(
    () => ({ confirmDelete, confirmShare, confirmUnshare, confirmClone, promptRename }),
    [confirmDelete, confirmShare, confirmUnshare, confirmClone, promptRename]
  );
}

export type AudienceDialogs = ReturnType<typeof useAudienceDialogs>;
